/**
 * 384, the shader warm-up (sweeps/2026-11-01-mega-cpu-phases.md): on gpuCheck's mega, 15–16 programs were compiled after
 * the load, each the first time its look showed — the markers, the ground's wear, a muzzle flash, the gunship's glow and
 * rockets, the explosions, the far smoke and lamps, a helicopter's glass, a sighting's blip, a shooting star — and the
 * frame it showed in waited on the compile (three's first use of a program reads its link). A throwaway of each such look
 * (its owner's `warmParts`) is handed to `renderer.compile` at the load, against the battle's scene (its lights and fog: in a
 * program's key) with the scene pass's target bound (its tone mapping and colour space: in the key too — without it the
 * programs come out under the screen's keys, which no draw in the composer uses); and the battle's scene itself with
 * them (three's compile walks hidden objects too: a look built at the load and first shown mid-fight). The programs link
 * while the battle starts (in parallel where the browser has KHR_parallel_shader_compile), and a later look of the same
 * settings takes the same program. The throwaways are dropped, never disposed, so three never releases what they compiled.
 * Presentation only: nothing is drawn, nothing in the sim moves.
 *
 * fix/384-warmup-race (lane E: 3 of 6 Story loads took the page down): three's compileAsync polls each material's program
 * from a timer and throws there — an uncaught error, past any catch — when a material it compiled was disposed before the
 * poll (`currentProgram` gone: a muzzle flash's throwaway released past its pool, a look the battle disposed in its first
 * moments). The poll is ours now: a material with no program, or a program that never reports ready within READY_LIMIT_MS,
 * counts as done; an error in it ends the poll (the look compiles at first use, as before the warm-up). It cannot take
 * the page down.
 *
 * 74.21 (lane I, the woods' hitch): a linked program is not a used one. three reads a program's link — getProgramInfoLog,
 * the uniform and attribute tables — at its first draw (WebGLProgram's onFirstUse), and flags every program ready at once
 * where the browser has no KHR_parallel_shader_compile ("it may cause a stall when it's first used", three's own note):
 * so the warm-up linked the woods' and the blips' programs and the first frame that drew each still waited 2–3 s on its
 * link read (the per-frame probe on viridis y0/y3 and thalia y1, the same with and without the woods in the warm-up). The
 * poll takes the first use now: a program that reports ready has its uniforms read there and then, at the load, so no
 * frame of the match reads a link.
 */
import * as THREE from 'three';
import type { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';

/**
 * What a warm-up did: the programs it made, the ms it held the main thread, the ms until every one was linked (null: not
 * yet), and the materials it compiled that had lost their program before they were ready (disposed: their type and name).
 */
export interface ProgramWarm { made: number; blockMs: number; readyMs: number | null; gone: string[] }

/** The poll's interval and its bound (ms): past the bound the warm-up stops waiting (readyMs stays null). */
const POLL_MS = 10, READY_LIMIT_MS = 10_000;

type ProgramOf = (m: THREE.Material) => { isReady(): boolean; getUniforms(): unknown } | undefined;

export function warmPrograms(o: { renderer: THREE.WebGLRenderer; scene: THREE.Scene; camera: THREE.Camera; composer: EffectComposer | null }, parts: readonly THREE.Object3D[]): ProgramWarm {
  const box = new THREE.Scene();
  for (const p of parts) box.add(p);
  const r = o.renderer, before = r.info.programs?.length ?? 0, t0 = performance.now(), was = r.getRenderTarget();
  const w: ProgramWarm = { made: 0, blockMs: 0, readyMs: null, gone: [] };
  let pending = new Set<THREE.Material>();
  try {
    r.setRenderTarget(o.composer ? o.composer.readBuffer : null);   // (the render pass draws the scene into it)
    pending = new Set([...r.compile(box, o.camera, o.scene), ...r.compile(o.scene, o.camera)]);
  } catch (e) {
    console.warn('384 warm-up: compile failed, the looks compile at first use', e);
  } finally {
    r.setRenderTarget(was);
  }
  w.made = (r.info.programs?.length ?? 0) - before;
  w.blockMs = performance.now() - t0;
  const props = (r as unknown as { properties?: { get(m: THREE.Material): { currentProgram?: { isReady(): boolean; getUniforms(): unknown } } } }).properties;
  const programOf: ProgramOf = m => props?.get(m).currentProgram;
  pollReady(pending, programOf, t0, w);
  return w;
}

/**
 * Until every material's program is linked (or gone, or past the bound), every POLL_MS: then `w.readyMs`. A program that is
 * ready has its first use taken here (74.21: the link read three would do on its first draw). Never throws.
 */
function pollReady(pending: Set<THREE.Material>, programOf: ProgramOf, t0: number, w: ProgramWarm): void {
  const poll = (): void => {
    try {
      for (const m of pending) {
        const p = programOf(m);
        if (!p && w.gone.length < 8) w.gone.push(`${m.type}${m.name ? ' ' + m.name : ''}`);
        if (!p) { pending.delete(m); continue; }
        if (p.isReady()) { p.getUniforms(); pending.delete(m); }
      }
    } catch {
      pending.clear();   // (the poll is over; what isn't linked links at first use)
      return;
    }
    if (pending.size === 0) { w.readyMs = performance.now() - t0; return; }
    if (performance.now() - t0 < READY_LIMIT_MS) setTimeout(poll, POLL_MS);
  };
  poll();
}
